import React from 'react';
import {
	Header as HeaderDI,
	Section as SectionDI,
	SkeletonItem as SkeletonItemDI,
} from '@atlaskit/side-navigation';
import { JSErrorBoundary as JsErrorBoundaryDI } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useBoard } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import ProjectShortcuts from '../../../common/ui/project-shortcuts/index.tsx';
import AgileReportsDI from './agile/main.tsx';
import { DevOpsReports as DevOpsReportsDI } from './dev-ops/index.tsx';
import ForecastManagementReportsDI from './forecast-management/main.tsx';
import IssueAnalysisReportsDI from './issue-analysis/main.tsx';
import { messages } from './messages.tsx';
import OtherReportsDI from './other/main.tsx';
import type { ReportsProps } from './types.tsx';

const useThrowProjectDI = withThrowOnError(useProjectDI);

export const Reports = ({
	Header = HeaderDI,
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	// eslint-disable-next-line react-hooks/react-compiler
	useIntl = useIntlDI,
	// eslint-disable-next-line react-hooks/react-compiler
	useProject = useThrowProjectDI,
	AgileReports = AgileReportsDI,
	DevOpsReports = DevOpsReportsDI,
	JsErrorBoundary = JsErrorBoundaryDI,
	IssueAnalysisReports = IssueAnalysisReportsDI,
	ForecastManagementReports = ForecastManagementReportsDI,
	OtherReports = OtherReportsDI,
	connectItems,
}: ReportsProps) => {
	// eslint-disable-next-line react-hooks/react-compiler
	const { formatMessage } = useIntl();
	// eslint-disable-next-line react-hooks/react-compiler
	const { data: project } = useProject();
	const { data: board, loading: isBoardLoading, error } = useBoard();

	if (project == null) return <SkeletonItem />;
	if (isBoardLoading && fg('reports-overview-page-migration')) return <SkeletonItem />;

	// Team solaris should be monitoring this, not navigation team.
	const devOpsErrorBoundaryProps = {
		teamName: 'fusion-solaris',
		// We don't want to blow-up anything, but just not to render devOps nav group only.
		fallback: 'unmount',
		packageName: 'navigationAppsSidebarSoftwareClassic',
		id: 'classicDevOpsMenuGroup',
	};

	const newReportsHref =
		board == null && !isBoardLoading && !error
			? `/jira/software/c/projects/${project.key}/reports`
			: `/jira/software/c/projects/${project.key}/boards/${board?.id}/reports`;
	const reportsHref = fg('reports-overview-page-migration')
		? newReportsHref
		: `/projects/${project.key}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`;

	return (
		<Section>
			<Header>{formatMessage(messages.title)}</Header>
			<MenuItem
				analytics={{ itemId: 'all' }}
				selectedOn={[
					...(fg('reports-overview-page-migration')
						? (['software-reports', 'software-reports-boardless'] as const)
						: []),
					'legacy-all-reports',
				]}
				href={reportsHref}
			>
				{formatMessage(messages.allReports)}
			</MenuItem>
			<AgileReports />
			{/* @ts-expect-error - TS2769 - No overload matches this call. */}
			<JsErrorBoundary {...devOpsErrorBoundaryProps}>
				<DevOpsReports />
			</JsErrorBoundary>
			<IssueAnalysisReports />
			<ForecastManagementReports />
			<OtherReports connectItems={connectItems} />
			{getWillShowNav3() && <ProjectShortcuts project={project} />}
		</Section>
	);
};
